import React, { createElement, useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next';
import isEqual from 'lodash/isEqual'
import {
    FlexContainer, TitleH5, Paragraph, isEmpty, switchValueByType
} from 'heliocor-ui'

import {
    REQUEST_INSERT_OWNER, REQUEST_UPDATE_OWNER, ACTIVATE_CUSTOM_NAVIGATION, DELETE_CURRENT_OWNER_DOC,
    DELETE_ALL_CURRENT_DOCS, SET_CURRENT_OWNER_DOCS, SET_CAFE_STATUS, SET_NOTIFICATION, DELETE_POST_ERROR
} from '../../store/cafe/actionTypes'

import { Accordion, AccordionProvider } from '../../common'
import CommonTitle from '../../common/CommonTitle'
import { db } from '../../api/db'
import { generateStructure } from './Structure.generator'
import { isCategoryConditionSatisfied } from './helpers'
import { areFormErrorsEmpty } from '../../utils/forms'
import { getOwnerRequestedDocs } from './helpers'

const ManageEntity = ({
    isLookup,
    isEdit,
    typeOfEntity,
    selectedEntity = {},
    setTypeOfEntity,
    setNextView,
    updateNavigationButtons,
    wizardContainerRef,
    defaultValues = {},
    ownerCanBeEdited,
    setStructureData,
}) => {

    const { t } = useTranslation()
    const typeAndRelation = useRef(null)
    const details = useRef(null)
    const editEntity = useRef(null)
    const documents = useRef(null)
    const valuesRef = useRef(null)

    const accordionRefs = {
        typeAndRelation,
        details,
        documents,
        editEntity,
    }

    const cafeStatus = useSelector(store => store.general.cafeStatus)
    const cpCategories = useSelector(store => store.settings.cpCategories)
    const currentOwnerDocs = useSelector(store => store.currentOwnerDocs)
    const formattingOptions = useSelector(store => store.settings.formattingOptions)
    const isRequest = useSelector(store => store.config.isRequest)
    const navigation = useSelector(store => store.navigation)
    const party = useSelector(store => store.party)
    const phase = useSelector(store => store.config.phase)
    const preApprovalDocs = useSelector(store => store.settings.dataParties)
    const supportedCountries = useSelector(store => store.settings.supportedCountries)
    const supportedRegions = useSelector(store => store.settings.supportedRegions)
    const includeStructure = useSelector(store => store.settings.includeStructure)
    const postErrors = useSelector(store => store.party.postErrors || [])
    const authorizedSignatoryDocs = useSelector(store => store.settings.authSignatoryDocumentTypes) || []
    const directorDocs = useSelector(store => store.settings.directorDocumentTypes) || []
    const shareholdersDocs = useSelector(store => store.settings.shareholderDocumentTypes) || []
    const uboDocs = useSelector(store => store.settings.uboDocumentTypes) || []
    const fieldsConfig = useSelector(state => state.settings.cpFields[`CONNECTED_${newEntityType}`])

    const dispatch = useDispatch()
    const activateCustomNavigation = (enabled) => dispatch({ type: ACTIVATE_CUSTOM_NAVIGATION, enabled })
    const deleteAllCurrentOwnerDocs = () => dispatch({ type: DELETE_ALL_CURRENT_DOCS })
    const deleteDocument = (docType) => dispatch({ type: DELETE_CURRENT_OWNER_DOC, docType })
    const insertOnwer = (newEntityType, owner, comeFromLookup) => dispatch({ type: REQUEST_INSERT_OWNER, newEntityType, owner, comeFromLookup })
    const sendNotification = (notification) => dispatch({ type: SET_NOTIFICATION, notification })
    const setCafeStatus = (cafeStatus) => dispatch({ type: SET_CAFE_STATUS, cafeStatus: cafeStatus })
    const setCurrentOwnerDocs = (docs) => dispatch({ type: SET_CURRENT_OWNER_DOCS, docs })
    const updateOwners = (owner) => dispatch({ type: REQUEST_UPDATE_OWNER, owner })
    const deletePostError = (fieldId, identifier) => dispatch({ type: DELETE_POST_ERROR, fieldId, identifier })

    const [errors, setErrors] = useState({})
    const [indexForm, setIndexForm] = useState(0)
    const [isLoadingOwnerTask, setIsLoadingOwnerTask] = useState(false)
    const [totalOwnership, setTotalOwnership] = useState(0)
    const [newEntityType, setNewEntityType] = useState(null)
    const [values, setValues] = useState({ documents: currentOwnerDocs })
    const [structure, setStructure] = useState()
    const [mergedKYCDocs, setMergedKYCDocs] = useState([])

    const isLoading = ['UPDATING_OWNER', 'CREATING_OWNER'].includes(cafeStatus)
    const cpType = newEntityType === 'PERSON' ? 'CONNECTED_PERSON' : 'CONNECTED_COMPANY'
    const noFormErrors = !!areFormErrorsEmpty(errors)

    const initialValues = { ...selectedEntity.entity, parentId: selectedEntity.entity?.parentId }
    const areValuesEqual = isEqual(values, initialValues)

    useEffect(() => {
        valuesRef.current = values
    }, [values])

    const calculateOwnership = () => {
        const { connectedParties } = party
        const parent = connectedParties.find(cp => cp.identifier === values.parentId)
        const structureLevel = (parent?.level + 1) || 0
        const isChildren = (parentId) => !parentId || parentId === values.parentId

        const ownership = connectedParties
            .filter(cp => cp?.level === structureLevel && isChildren(cp.parentId))
            .reduce((acc, cp) => acc + (cp.ownership || 0), 0)

        setTotalOwnership(100 - ownership)
    }

    //TODO; Re-think and do
    const checkIfThereAreErrors = (entityType) => false

    const cleanValues = () => {
        setValues(prev => ({
            name: prev.name,
            registeredAddressCountry: prev.registeredAddressCountry,
            ownership: prev.ownership,
            externalIdentifier: null
        }))
    }

    const cleanDependantFields = (fieldId) => {
        Object.values(cpCategories[cpType])
            .filter(cat => cat.code !== "TYPE_OF_RELATION")
            .forEach(cat => {
                const dependency = cat?.dependencies || {}
                const isConditionalCategory = dependency?.field === fieldId
                const isCategorySatisfied = isCategoryConditionSatisfied({dependency, values, isRequest})

                if(isConditionalCategory) {
                    cat.fields.forEach(f => {
                        if (!isCategorySatisfied) {
                            setValues(prev => ({ ...prev, [f.id]: f.type === 'CHECK' ? false : undefined }))
                            setErrors(prev => ({ ...prev, [f.id]: undefined }))
                        }
                    })
                }
        })
    }

    const errorHandler = newErrors => {
        if (Object.keys(newErrors).length && !Object.values(newErrors)[0].length) {
            setErrors(prev => {
                const errorUpdate = { ...prev }
                delete errorUpdate[Object.keys(newErrors)]
                return errorUpdate
            })
        } else {
            setErrors(prev => ({ ...prev, ...newErrors }))
        }
    }

    const inputChangedHandler = (id, value) => {
        const type = Object.values(cpCategories[cpType])
        .filter(c => c.code !== "TYPE_OF_RELATION")
        .reduce((type, cat) => type || cat.fields.find(f => f.id === id)?.type, null)

        if (['SELECT', 'CHECK'].includes(type)) {
            cleanDependantFields(id)
        }
        setValues(prev => ({ ...prev, [id]: value }))
    }

    //TODO: Improve 
    const relationChangeHandler = (id, value, clean) => {
        
        let ownership
        if (((id === "shareholder" && !valuesRef.current.ubo) || (id === "ubo" && !valuesRef.current.shareholder)) && value === false) {
            ownership = 0
        } else if (id === 'ownership') {
            ownership = value
        } else {
            ownership = valuesRef.current.ownership
        }

        setValues(prev => ({
            ...prev,
            ownership,
            [id]: value
        }))

        if (clean) cleanData({ id, value })
    }

    const cleanData = ({ id, value }) => {
        const { authorizedSignatory, director, shareholder, ubo } = valuesRef.current

        const newMergedDocs = getOwnerRequestedDocs(
            newEntityType, preApprovalDocs, isRequest,
            { authorizedSignatoryDocs, directorDocs, shareholdersDocs, uboDocs },
            { authorizedSignatory, director, shareholder, ubo, [id]: value },
            fieldsConfig, values.documents || [])
            .reduce((list, cat) => {
                cat.documents.forEach(doc => list.push(doc.name))
                return list
            }, [])
        
        //eslint-disable-next-line
        selectedEntity?.entity.documents.forEach(doc => {
            if (!newMergedDocs.includes(doc.type)) {
                deleteDocument(doc.type)
            }
        })
    }

    const onSelectSubsidiary = (value, level = 0) => {
        setValues(prev => ({ ...prev, parentId: value, level: level + 1 }))
    }

    const handleCancelCParty = () => {
        setValues({})
        deleteAllCurrentOwnerDocs()
        if (indexForm === 0) setNextView('STRUCTURE')
    }

    const handleFinishCParty = () => {
        if (isLookup) {
            insertOnwer(values.type, {...values, parentId: 'parent', level: 0 }, true)
        } else {
            isEdit ? updateOwners(values) : insertOnwer(newEntityType, values)
        }
    }

    const setTypeOfNewEntity = (value) => {
        setErrors({})
        setNewEntityType(value)
    }

    const setLookupType = (value) => {

        const formFields = {
            birthDate: 'registrationDate',
            addressCity: 'registeredAddressCity',
            addressCountry: 'registeredAddressCountry',
            addressRegion: 'registeredAddressRegion',
            addressPostalCode: 'registeredAddressPostalCode',
            addressStreet: 'registeredAddressStreet',
            addressStreet2: 'registeredAddressStreet2',
        }

        let newValues = switchValueByType({
            originalValues: values,
            fieldsToInterchange: formFields,
            types: ['PERSON', 'COMPANY'],
            typeToSwitch: value
        })

        setValues({ ...newValues, type: value })
    }

    const updateNavigationInStructure = () => {
        const customNavigationIsEdit = [{
            id: 'button.cancel',
            onClick: handleCancelCParty,
            value: t('button.cancel'),
            type: 'light',
            margin: '0 16px 0 0'
        },
        {
            id: 'button.save',
            onClick: handleFinishCParty,
            value: t('button.save'),
            margin: '0 16px 0 0',
            disabled: isLoading || !areFormErrorsEmpty(errors) || areValuesEqual || (isLookup && (!newEntityType || !values?.type)) || ['authorizedSignatory', 'shareholder', 'director', 'ubo'].every(id => !values[id]),
            isLoading: isLoading || isLoadingOwnerTask
        }] 

        updateNavigationButtons(customNavigationIsEdit)
    }

    const generateEntityStructure = () => {
        const { registeredAddressCountry, shareholder, ubo } = values
        const country = registeredAddressCountry?.value
        const isOwnershipAllowed = shareholder || ubo

        const entityStructure = generateStructure({
            phase,
            includeStructure,
            errorHandler,
            relationChangeHandler,
            onSelectSubsidiary,
            setTypeOfNewEntity,
            typeOfNewEntity: newEntityType,
            setLookupType,
            setTypeOfEntity,
            isEdit,
            isLookup,
            isOwnershipAllowed,
            isLoading: false,
            cpCategories,
            supportedRegions,
            country,
            cleanValues,
            inputChangedHandler,
            isRequest
        })

        setStructure(entityStructure)
    }

    const createCompanyLookupDependencies = () => {

        return ({
            customFieldsArray: [
                'name',
                'registrationDate',
                'registeredAddressCity',
                'registeredAddressCountry',
                'registeredAddressRegion',
                'registeredAddressPostalCode',
                'registeredAddressStreet',
                'registeredAddressStreet2'
            ],
            setStructureData,
            supportedCountries,
            collectStructure: navigation.stepsList[navigation.stepIndex] !== 'STRUCTURE',
            url: {
                checkCompanyHouse: ({ name, registeredAddressCountry, registrationNumber, region }) =>
                    `dokstor-cafe-api/api/search/companies/search?name=${name}&country=${registeredAddressCountry}&registration-number=${registrationNumber}${region ? `&region=${region}` : ''}`
            },
            sendNotification,
            db
        })
    }

    const handlePostError = fieldId => deletePostError(fieldId, values.identifier)

    const setOwnerRequestedDocs = () => {
        const { authorizedSignatory, director, shareholder, ubo } = values
        const mergedKYC = getOwnerRequestedDocs(
            newEntityType, preApprovalDocs, isRequest,
            { authorizedSignatoryDocs, directorDocs, shareholdersDocs, uboDocs },
            { authorizedSignatory, director, shareholder, ubo },
            fieldsConfig, values.documents || [])

        if (Object.keys(mergedKYC).some(cat => (mergedKYC[cat].numberOfDocsToUpload) > 0)) { 
            errorHandler({ minDocs: 'need to upload more docs' })
        } else {
            errorHandler({ minDocs: [] })
        }

        setMergedKYCDocs(mergedKYC)
    }
    
    useEffect(() => {
        if (isEdit) {
            const _entity = selectedEntity?.entity || {}
            setValues(prev => ({ ...prev, ..._entity }))
            if (!isRequest && _entity.documents) {
                setCurrentOwnerDocs(_entity.documents)
            }
        }

        if (!isEdit) setValues(defaultValues)

        if (includeStructure && phase === 'self-onboarding' && !isEdit) {
            setValues({ parentId: 'parent', level: 0 })
        }

        calculateOwnership()
        generateEntityStructure()
    }, [])


    useEffect(() => {
        if (values.phone === '' && errors?.phone) {
            setErrors(prev => {
                const _errors = { ...prev }
                delete _errors.phone
            })
        }
    }, [values.phone, errors?.phone])

    useEffect(() => {
        !isLookup && setValues(prev => (
            { ...defaultValues, ...prev, parentId: prev.parentId, level: prev.level }
        ))
        !!newEntityType && !isEdit && deleteAllCurrentOwnerDocs()
        generateEntityStructure()
    }, [newEntityType])

    useEffect(() => {
        setValues(prev => ({ ...prev, documents: currentOwnerDocs }))
    }, [currentOwnerDocs])

    useEffect(() => {
        generateEntityStructure()
    }, [isLookup, values.registeredAddressCountry, values.type, values.shareholder, values.ubo, values.ownership])

    useEffect(() => {
        calculateOwnership()
    }, [values.parentId])

    useEffect(() => {
        if (['OWNER_UPDATED', 'OWNER_CREATED'].includes(cafeStatus)) {
            setIndexForm(0)
            setValues({})
            setNewEntityType(null)
            setNextView('STRUCTURE')
            deleteAllCurrentOwnerDocs()
            setCafeStatus('READY')
            setIsLoadingOwnerTask(false)
        }

        if (['UPDATING_OWNER', 'CREATING_OWNER'].includes(cafeStatus) && !isLoadingOwnerTask) {
            setIsLoadingOwnerTask(true)
            updateNavigationInStructure()
        }
    }, [cafeStatus, isLoadingOwnerTask])

    useEffect(() => {
        updateNavigationInStructure()
    }, [noFormErrors, areValuesEqual, values?.documents?.length])
    useEffect(() => {
        setValues(prev => ({
            ...prev,
            documents: currentOwnerDocs
        }))
    }, [currentOwnerDocs])

    useEffect(() => {
        return () => {
            setValues({})
            deleteAllCurrentOwnerDocs()
            activateCustomNavigation(false)
        }
    }, [])

    useEffect(setOwnerRequestedDocs, [values, newEntityType])

    const createEntityPage = structure && [{
        id: 'typeAndRelation',
        ref: accordionRefs.typeAndRelation,
        render: structure.selectEntity,
        description: 'warning.pleaseChooseEntity',
        isOpen: true,
        isFree: true
    }, {
        id: 'details',
        ref: accordionRefs.details,
        render: newEntityType ? structure[newEntityType] : null,
        description: 'warning.addDetailsHere',
        disabled: !newEntityType || (['authorizedSignatory', 'shareholder', 'director', 'ubo'].every(id => !values[id])),
        isOpen: newEntityType && ['authorizedSignatory', 'shareholder', 'director', 'ubo'].some(id => values[id]),
        isFree: true,
    }, {
        id: 'documents',
        ref: accordionRefs.documents,
        render: structure.ownerDocs,
        description: checkIfThereAreErrors(newEntityType) ? 'warning.fillRequiredFields' : 'warning.uploadOwnerDocuments',
        isFree: true,
        higherAccordion: true,
        disabled: newEntityType ? checkIfThereAreErrors(newEntityType) : true,
        noPrint: !isRequest && isEmpty(preApprovalDocs)
    }]

    const editEntityPage = structure && [{
        id: 'typeAndRelation',
        ref: accordionRefs.typeAndRelation,
        render: structure.selectEntity,
        description: 'warning.pleaseChooseEntity',
        isFree: true,
    }, {
        id: values?.type === 'PERSON' ? 'editConnectedPerson' : 'editConnectedCompany',
        ref: accordionRefs.editEntity,
        render: structure[typeOfEntity],
        disabled: !typeOfEntity || (['authorizedSignatory', 'shareholder', 'director', 'ubo'].every(id => !values[id])),
        description: 'warning.addDetailsHere',
        isFree: true
    }, {
        id: 'documents',
        ref: accordionRefs.documents,
        render: structure.ownerDocs,
        description: checkIfThereAreErrors(typeOfEntity) ? 'warning.fillRequiredFields' : 'warning.uploadOwnerDocuments',
        isFree: true,
        higherAccordion: true,
        disabled: typeOfEntity ? checkIfThereAreErrors(typeOfEntity) : true,
        noPrint: !isRequest && isEmpty(preApprovalDocs),
    }]

    const accordions = (structure && isEdit ? editEntityPage : createEntityPage) || []

    const nameToRender = values.surname ? `${values.name}  ${values.surname}` : values.name

    const cpPostErrors = postErrors.filter(e => e.identifier === values.identifier)

    return (
        <AccordionProvider>
            <CommonTitle
                customStyle='text-transform: capitalize;'
                value={isEdit ? nameToRender : t('addEntity')}
            />
            {accordions.map((acc, index) => (
                !acc.noPrint &&
                <Accordion
                    ref={acc.ref}
                    higherAccordion={acc.higherAccordion}
                    isOpen={acc.isOpen}
                    id={acc.id}
                    isFree={acc.isFree}
                    disabled={acc.disabled}
                    key={index}
                    index={index}
                    wizardContainerRef={wizardContainerRef}
                    lastChild={accordions.length - 1 === index}
                    handleSelection={() => { }}
                    titleBox={
                        <FlexContainer column justifyContent='space-between' alignItems='baseline'>
                            <FlexContainer alignItems='center'>
                                <TitleH5 margin='0 .5rem 0 0' value={t(acc.id)} />
                            </FlexContainer>
                            <Paragraph textAlign='initial' margin='0.5rem 0 0' value={t(acc.description)} />
                        </FlexContainer>
                    }
                    contentBox={
                        <FlexContainer column>
                            {structure && !acc.render
                                ? null
                                : acc.render.component
                                    ? createElement(
                                        acc.render.component,
                                        {
                                            ...acc.render.props,
                                            values,
                                            errors,
                                            sliderMax: totalOwnership,
                                            canEdit: !(isRequest && ownerCanBeEdited),
                                            companyLookUpInstructions: createCompanyLookupDependencies(),
                                            formattingOptions,
                                            mergedKYCDocs
                                        })
                                    : createElement(
                                        acc.render.form,
                                        {
                                            ...acc.render.props,
                                            values,
                                            errors,
                                            companyLookUpInstructions: createCompanyLookupDependencies(),
                                            sliderMax: totalOwnership,
                                            formattingOptions,
                                            postErrors: cpPostErrors,
                                            postErrorHandler: handlePostError
                                        }
                                    )
                            }
                        </FlexContainer>
                    }
                />
            ))}
        </AccordionProvider>
    )
}

export { ManageEntity }